import { session } from '@/components/helpers/session'
import { getAthosDomain, getAthosSiteID } from 'libs/athos/athos-utils'
import { cart } from '@/components/helpers/cart'
import { RANGE_INDICATOR } from '../components/collection/filters/FilterUtils'

/**
 * AthosParams
 * ---
 * Class designed to handle standardized formatting for any
 * URL manipulation SS might need for request building.
 * Extends on URLSearch Params.
 */

export class AthosParams extends URLSearchParams {
  siteID: string
  domain: string

  constructor(category?: string) {
    super()

    this.siteID = getAthosSiteID(category)
    this.domain = getAthosDomain()
  }

  /**
   * Coerce values to strings so numeric params (`resultsPerPage`, `limit`, …)
   * can be passed directly. Matches native `URLSearchParams` ToString behaviour.
   */
  set(name: string, value: string | number | boolean | undefined | null): void {
    super.set(name, `${value}`)
  }

  setSiteIDParam(): this {
    this.set('siteId', this.siteID)
    return this
  }

  setDomainParam(): this {
    this.set('domain', this.domain)
    return this
  }

  setViewedProducts() {
    const viewedProducts = session.viewedProducts.get()
    if (viewedProducts) this.set('lastViewed', String(viewedProducts))
  }

  setShopperID() {
    const id = session.shopper.get()
    if (id) this.set('shopper', String(id))
  }

  setUserID() {
    const id = session.userId.get()
    if (id) this.set('userId', id)
  }

  setSessionID() {
    const id = session.sessionId.get()
    if (id) this.set('sessionId', id)
  }

  setPageLoadID() {
    const id = session.pageLoadId.get()
    if (id) this.set('pageLoadId', id)
  }

  setCartItems() {
    const items = cart?.value?.items
    if (!items) return

    const skus = items.map((item) => item.sku).join(',')
    if (skus) this.set('cart', skus)
  }

  setParams(params: Record<string, string | number>): this {
    for (const [key, values] of Object.entries(params)) {
      this.set(key, values)
    }
    return this
  }

  setSort(sort: string): void {
    this.clearSort()

    const [field, direction] = sort.split(':')
    const key = `sort.${field}`
    this.set(key, direction)
  }

  clearSort(): void {
    const keys = Array.from(this.keys())
    for (const key of keys) {
      if (key.startsWith('sort')) {
        this.delete(key)
      }
    }
  }

  setFilters(filters?: Set<string> | null): this {
    const keys = Array.from(this.keys())
    for (const key of keys) {
      if (key.startsWith('filter')) {
        this.delete(key)
      }
    }

    if (!filters || !(filters instanceof Set)) {
      return this
    }

    filters.forEach((filter) => {
      if (!filter || typeof filter !== 'string') {
        return
      }

      const parts = filter.split(':')
      if (parts.length < 2) {
        return
      }

      const [field, ...valueParts] = parts
      const value = valueParts.join(':')

      if (!field || !value) {
        return
      }

      const key = `filter.${field}`

      if (value.includes(RANGE_INDICATOR)) {
        const [low, high] = value.split(RANGE_INDICATOR)
        this.append(`${key}.low`, low)
        this.append(`${key}.high`, high)
        return
      }

      this.append(key, value)
    })

    return this
  }
}
